'use client'

import React from 'react'
import { Search, Bell, User, LogOut, ChevronDown, Smartphone, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useSidebarStore } from '@/hooks/useSidebar'
import { ThemeToggle } from './ThemeToggle'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet'
import { AdminSidebar } from './AdminSidebar'
import { UserSidebar } from './UserSidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/AuthContext'
import { useRouter, usePathname } from 'next/navigation'
import { userNavItems, adminNavItems, filterNavItems, type NavItem } from './nav-config'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import { toast } from 'sonner'

function ClientSearchNavigation() {
  const [query, setQuery] = React.useState('')
  const [isOpen, setIsOpen] = React.useState(false)
  const [isExpanded, setIsExpanded] = React.useState(false)
  const router = useRouter()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const flatItems = React.useMemo(() => {
    const items: Array<{ label: string; href: string; icon: any; section?: string }> = []
    const seen = new Set<string>()

    function traverse(list: NavItem[], parentSection?: string) {
      for (const item of list) {
        if (item.href && !seen.has(item.href)) {
          seen.add(item.href)
          items.push({
            label: item.label,
            href: item.href,
            icon: item.icon,
            section: parentSection || item.section,
          })
        }
        if (item.children && item.children.length > 0) {
          traverse(item.children, item.label)
        }
      }
    }

    traverse(userNavItems)
    return items
  }, [])

  const filteredItems = React.useMemo(() => {
    if (!query.trim()) return []
    const lowerQuery = query.toLowerCase()
    return flatItems.filter(item => 
      item.label.toLowerCase().includes(lowerQuery) ||
      (item.section && item.section.toLowerCase().includes(lowerQuery))
    )
  }, [query, flatItems])

  const handleSelect = (href: string) => {
    router.push(href)
    setQuery('')
    setIsOpen(false)
    setIsExpanded(false)
  }

  const handleExpand = () => {
    setIsExpanded(true)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  const handleCollapse = () => {
    setIsExpanded(false)
    setQuery('')
    setIsOpen(false)
  }

  return (
    <>
      {/* Mobile: icon button → expands to full overlay input */}
      <div className="flex md:hidden items-center">
        {isExpanded ? (
          <div className="relative flex items-center gap-2">
            <div className="relative flex items-center">
              <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                value={query}
                onChange={(e) => { setQuery(e.target.value); setIsOpen(true) }}
                onBlur={() => setTimeout(() => { setIsOpen(false); if (!query) setIsExpanded(false) }, 200)}
                className="w-[180px] h-9 pl-9 pr-3 rounded-xl bg-muted/60 border border-border text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all placeholder:text-muted-foreground/50 font-medium"
              />
              {/* Dropdown */}
              {isOpen && filteredItems.length > 0 && (
                <div className="absolute top-full left-0 w-[220px] mt-2 bg-popover border border-border rounded-xl shadow-2xl z-[100] overflow-hidden py-1 max-h-[260px] overflow-y-auto">
                  {filteredItems.map((item) => {
                    const Icon = item.icon
                    return (
                      <button
                        key={item.href}
                        onMouseDown={() => handleSelect(item.href!)}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-left text-sm text-foreground hover:bg-muted/80 transition-colors"
                      >
                        {Icon && (
                          typeof Icon === 'string' ? (
                            <img src={Icon} alt="" className="h-4 w-4 object-contain dark:invert shrink-0" />
                          ) : (
                            <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                          )
                        )}
                        <div className="flex flex-col min-w-0">
                          <span className="font-semibold text-foreground truncate">{item.label}</span>
                          {item.section && (
                            <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider mt-0.5 truncate">
                              {item.section}
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
            <button
              onClick={handleCollapse}
              className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-muted/70 text-muted-foreground transition-colors"
            >
              <span className="text-xs font-bold">✕</span>
            </button>
          </div>
        ) : (
          <button
            onClick={handleExpand}
            className="h-9 w-9 flex items-center justify-center rounded-full border border-border/85 bg-background shadow-sm hover:bg-muted transition-colors"
          >
            <Search className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Desktop: always-visible input */}
      <div className="relative hidden md:block w-full max-w-sm">
        <div className="relative flex items-center">
          <Search className="absolute left-3.5 h-[18px] w-[18px] text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setIsOpen(true) }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            className="w-full h-10 pl-[38px] pr-4 rounded-xl bg-background border border-border shadow-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary placeholder:text-muted-foreground/60 transition-all text-sm font-medium"
          />
        </div>
        {isOpen && filteredItems.length > 0 && (
          <div className="absolute top-full left-0 w-full mt-2 bg-popover border border-border rounded-xl shadow-2xl z-[100] overflow-hidden py-1 max-h-[300px] overflow-y-auto">
            {filteredItems.map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.href}
                  onMouseDown={() => handleSelect(item.href!)}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-left text-sm text-foreground hover:bg-muted/80 transition-colors"
                >
                  {Icon && (
                    typeof Icon === 'string' ? (
                      <img src={Icon} alt="" className="h-4 w-4 object-contain dark:invert shrink-0" />
                    ) : (
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    )
                  )}
                  <div className="flex flex-col min-w-0">
                    <span className="font-semibold text-foreground truncate">{item.label}</span>
                    {item.section && (
                      <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider mt-0.5 truncate">
                        {item.section}
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}

function AdminBreadcrumb() {
  const pathname = usePathname()

  const currentPageTitle = React.useMemo(() => {
    if (!pathname || pathname === '/admin' || pathname === '/admin/overview') {
      return 'Dashboard'
    }

    // Match exact or prefix in adminNavItems
    for (const item of adminNavItems) {
      if (item.href === pathname) return item.label
      if (item.children) {
        for (const child of item.children) {
          if (child.href === pathname) return child.label
          if (pathname.startsWith(child.href + '/')) return child.label
        }
      }
      if (item.href && pathname.startsWith(item.href + '/')) return item.label
    }

    // Fallback: extract last segment and format
    const segments = pathname.split('/').filter(Boolean)
    const last = segments[segments.length - 1] || 'Dashboard'
    return last
      .split(/[-_]/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  }, [pathname])

  return (
    <div className="hidden sm:flex items-center gap-2 select-none">
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Admin</span>
      <span className="text-xs text-slate-300 dark:text-slate-600 font-light">/</span>
      <span className="px-2.5 py-1 rounded-lg bg-slate-100/90 dark:bg-slate-800/90 text-slate-800 dark:text-slate-100 font-bold text-xs tracking-tight shadow-2xs border border-slate-200/50 dark:border-slate-700/50">
        {currentPageTitle}
      </span>
    </div>
  )
}

function AdminSearchNavigation() {
  const [query, setQuery] = React.useState('')
  const [isOpen, setIsOpen] = React.useState(false)
  const [isExpanded, setIsExpanded] = React.useState(false)
  const router = useRouter()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const { hasFeatureAccess, isLoading: authLoading, roleSlug, features } = useAuth()

  // Cmd+K / Ctrl+K keyboard shortcut listener
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        setIsOpen(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Filter accessible items based on roleSlug and features
  const accessibleNavItems = React.useMemo(() => {
    if (authLoading || (roleSlug === null && features === null)) {
      return adminNavItems
    }
    return filterNavItems(adminNavItems, hasFeatureAccess, roleSlug)
  }, [hasFeatureAccess, authLoading, roleSlug, features])

  // Flatten accessible admin items (including children) for search
  const flatItems = React.useMemo(() => {
    const items: Array<{ label: string; href: string; icon: any; section?: string }> = []
    const seen = new Set<string>()

    function traverse(list: NavItem[], parentSection?: string) {
      for (const item of list) {
        if (item.href && !seen.has(item.href)) {
          seen.add(item.href)
          items.push({
            label: item.label,
            href: item.href,
            icon: item.icon,
            section: parentSection || item.section,
          })
        }
        if (item.children && item.children.length > 0) {
          traverse(item.children, item.label)
        }
      }
    }

    traverse(accessibleNavItems)
    return items
  }, [accessibleNavItems])

  const filteredItems = React.useMemo(() => {
    if (!query.trim()) return []
    const lowerQuery = query.toLowerCase()
    return flatItems.filter(item => 
      item.label.toLowerCase().includes(lowerQuery) ||
      (item.section && item.section.toLowerCase().includes(lowerQuery))
    )
  }, [query, flatItems])

  const handleSelect = (href: string) => {
    router.push(href)
    setIsOpen(false)
    setQuery('')
    setIsExpanded(false)
  }

  const handleExpand = () => {
    setIsExpanded(true)
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  const handleCollapse = () => {
    setIsExpanded(false)
    setQuery('')
    setIsOpen(false)
  }

  return (
    <>
      {/* Mobile: icon button → expands to overlay input */}
      <div className="flex md:hidden items-center">
        {isExpanded ? (
          <div className="relative flex items-center gap-2">
            <div className="relative flex items-center">
              <Search className="absolute left-3 h-3.5 w-3.5 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                value={query}
                onChange={(e) => { setQuery(e.target.value); setIsOpen(true) }}
                onBlur={() => setTimeout(() => { setIsOpen(false); if (!query) setIsExpanded(false) }, 200)}
                className="w-[180px] h-9 pl-8 pr-3 rounded-xl bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 text-xs text-slate-800 dark:text-slate-100 outline-none focus:ring-1 focus:ring-[#00E599]/60 font-medium"
              />
              {/* Dropdown */}
              {isOpen && query.trim() && (
                <div className="absolute top-full right-0 w-[240px] mt-2 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl z-[100] overflow-hidden py-1 max-h-[260px] overflow-y-auto">
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item) => {
                      const Icon = item.icon
                      return (
                        <button
                          key={item.href}
                          onMouseDown={() => handleSelect(item.href)}
                          className="flex items-center gap-3 w-full px-3 py-2 text-left text-xs text-foreground hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                          {Icon && (
                            typeof Icon === 'string' ? (
                              <img src={Icon} alt="" className="h-3.5 w-3.5 object-contain dark:invert shrink-0" />
                            ) : (
                              <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            )
                          )}
                          <div className="flex flex-col min-w-0">
                            <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{item.label}</span>
                            {item.section && (
                              <span className="text-[9px] text-slate-400 uppercase font-bold tracking-wider mt-0.5 truncate">
                                {item.section}
                              </span>
                            )}
                          </div>
                        </button>
                      )
                    })
                  ) : (
                    <div className="px-4 py-3 text-xs text-muted-foreground text-center">
                      No results
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={handleCollapse}
              className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-slate-200/70 text-slate-500 transition-colors"
            >
              <span className="text-xs font-bold">✕</span>
            </button>
          </div>
        ) : (
          <button
            onClick={handleExpand}
            className="h-9 w-9 flex items-center justify-center rounded-xl border border-slate-200/60 dark:border-slate-700/60 bg-slate-100/70 dark:bg-slate-800/60 text-slate-500 hover:bg-slate-200/80 transition-colors shadow-2xs"
            aria-label="Search"
          >
            <Search className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Desktop: floating search input with Cmd+K shortcut */}
      <div className="relative hidden md:block">
        <div className="relative flex items-center">
          <Search className="absolute left-3 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search Pages..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setIsOpen(true) }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            className="w-[240px] sm:w-[280px] lg:w-[320px] h-9 pl-9 pr-11 rounded-xl bg-slate-100/70 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 text-xs text-slate-800 dark:text-slate-100 placeholder:text-slate-400 font-medium focus:outline-none focus:ring-1 focus:ring-[#00E599]/50 focus:border-[#00E599]/60 transition-all shadow-2xs"
          />
          <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none inline-flex items-center px-1.5 py-0.5 rounded border border-slate-200/80 dark:border-slate-700 bg-white/80 dark:bg-slate-800 text-[10px] font-mono font-semibold text-slate-400 shadow-2xs">
            ⌘K
          </kbd>
        </div>
        {isOpen && query.trim() && (
          <div className="absolute top-full right-0 w-[300px] sm:w-[320px] mt-2 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl z-[100] overflow-hidden py-1.5 max-h-[300px] overflow-y-auto animate-in fade-in-0 zoom-in-95 duration-150">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.href}
                    onMouseDown={() => handleSelect(item.href)}
                    className="flex items-center gap-3 w-full px-3.5 py-2 text-left text-xs text-foreground hover:bg-slate-100/80 dark:hover:bg-slate-800/80 transition-colors"
                  >
                    {Icon && (
                      typeof Icon === 'string' ? (
                        <img src={Icon} alt="" className="h-3.5 w-3.5 object-contain dark:invert shrink-0" />
                      ) : (
                        <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      )
                    )}
                    <div className="flex flex-col min-w-0">
                      <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{item.label}</span>
                      {item.section && (
                        <span className="text-[9px] text-slate-400 uppercase font-bold tracking-wider mt-0.5 truncate">
                          {item.section}
                        </span>
                      )}
                    </div>
                  </button>
                )
              })
            ) : (
              <div className="px-4 py-3 text-xs text-muted-foreground text-center">
                No accessible pages found
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

const GOOGLE_AVATAR_PALETTE = [
  '#1A73E8', // Google Blue
  '#D93025', // Google Red
  '#188038', // Google Green
  '#E37400', // Google Orange
  '#8E24AA', // Google Purple
  '#00897B', // Google Teal
  '#D81B60', // Google Pink
  '#3949AB', // Google Indigo
  '#F4511E', // Google Deep Orange
  '#00ACC1', // Google Cyan
  '#5E35B1', // Google Deep Purple
  '#6D4C41', // Google Brown
]

function getGoogleAvatarColor(text: string): string {
  if (!text) return GOOGLE_AVATAR_PALETTE[0]
  let hash = 0
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash)
  }
  const index = Math.abs(hash) % GOOGLE_AVATAR_PALETTE.length
  return GOOGLE_AVATAR_PALETTE[index]
}

export function Topbar() {
  const { toggle, isCollapsed, setCollapsed } = useSidebarStore()
  const { user, userType, roleSlug, clearAuth } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  
  const {
    isSupported,
    isIOS,
    isStandalone,
    needsHomeInstallation,
    permission,
    isSubscribed,
    loading,
    requestPermissionAndSubscribe,
    unsubscribe,
  } = usePushNotifications()

  const displayName = user?.fullName || user?.firstName || 'Admin User'
  const userInitial = (displayName.trim().charAt(0) || 'A').toUpperCase()
  const avatarColor = React.useMemo(() => {
    return getGoogleAvatarColor(displayName || user?.email || 'admin')
  }, [displayName, user?.email])

  const roleDisplay = React.useMemo(() => {
    const rawRole = (typeof user?.role === 'object' && user?.role !== null)
      ? (user.role as any).name
      : (roleSlug || user?.roleSlug || user?.role)

    if (!rawRole || typeof rawRole !== 'string') return 'Super Administrator'
    if (rawRole === 'super_admin') return 'Super Administrator'
    if (rawRole === 'admin') return 'Administrator'
    return rawRole
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }, [user, roleSlug])

  const handleLogout = () => {
    const tenantSlug = user?.tenant?.slug || (typeof window !== 'undefined' ? localStorage.getItem('last_tenant_slug') : null);
    clearAuth()
    if (typeof window !== 'undefined') {
      localStorage.clear()
      if (tenantSlug) {
        localStorage.setItem('last_tenant_slug', tenantSlug);
      }
    }
    const slugQuery = tenantSlug ? `?slug=${encodeURIComponent(tenantSlug)}` : '';
    const targetUrl = (userType === 'client' || userType === 'ib')
      ? `/client/login${slugQuery}`
      : `/admin/login${slugQuery}`;
    window.location.href = targetUrl;
  }

  const handleViewProfile = () => {
    if (userType === 'client') {
      router.push('/client/profile')
    } else {
      router.push('/admin/profile')
    }
  }

  const isClient = userType === 'client' || pathname?.startsWith('/client')

  const renderNotificationDropdownContent = () => (
    <DropdownMenuContent align="end" className="w-80 sm:w-96 p-4 border border-border/85 bg-popover shadow-xl rounded-2xl animate-in fade-in-0 zoom-in-95 duration-200">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm text-foreground flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" /> Notifications
          </h3>
          <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-semibold border ${
            isSubscribed 
              ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400' 
              : 'bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400'
          }`}>
            {isSubscribed ? 'Active' : 'Inactive'}
          </span>
        </div>
        
        <p className="text-xs text-muted-foreground leading-relaxed">
          Receive real-time push alerts for KYC status, deposits, withdrawals, and trading activity.
        </p>

        <DropdownMenuSeparator className="bg-border/60" />

        {needsHomeInstallation ? (
          <div className="flex flex-col gap-2.5 rounded-xl border border-sky-500/25 bg-sky-500/5 p-3 text-left">
            <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400 font-semibold text-xs">
              <Smartphone className="h-4 w-4 shrink-0" />
              <span>iOS Home Screen App Required</span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Apple requires web apps to be installed to the Home Screen for push notifications:
            </p>
            <ol className="text-[11px] text-foreground/90 space-y-1 list-decimal list-inside pl-1 font-medium">
              <li>Open Safari & tap the <span className="font-bold text-primary">Share</span> button</li>
              <li>Tap <span className="font-bold text-primary">Add to Home Screen</span></li>
              <li>Launch from Home Screen & enable alerts</li>
            </ol>
          </div>
        ) : isSupported ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between text-xs font-medium">
              <span className="text-muted-foreground">Browser Permission:</span>
              <span className={`capitalize font-semibold ${
                permission === 'granted' 
                  ? 'text-emerald-600 dark:text-emerald-400' 
                  : permission === 'denied' 
                    ? 'text-destructive' 
                    : 'text-amber-600 dark:text-amber-400'
              }`}>{permission}</span>
            </div>
            
            {isSubscribed ? (
              <Button 
                size="sm" 
                variant="outline" 
                className="w-full text-xs font-semibold text-destructive hover:text-destructive border-destructive/20 hover:bg-destructive/10 cursor-pointer h-9 rounded-lg"
                disabled={loading}
                onClick={async (e) => {
                  e.preventDefault();
                  await unsubscribe();
                  toast.success('Push notifications disabled.');
                }}
              >
                {loading ? 'Disabling...' : 'Disable Notifications'}
              </Button>
            ) : (
              <Button 
                size="sm" 
                className="w-full text-xs font-semibold cursor-pointer h-9 rounded-lg"
                disabled={loading || permission === 'denied'}
                onClick={async (e) => {
                  e.preventDefault();
                  const success = await requestPermissionAndSubscribe();
                  if (success) {
                    toast.success('Push notifications enabled successfully!');
                  } else {
                    toast.error('Failed to enable push notifications.');
                  }
                }}
              >
                {loading ? 'Enabling...' : 'Enable Notifications'}
              </Button>
            )}
            
            {permission === 'denied' && (
              <p className="text-[10px] text-destructive leading-normal text-center">
                Notifications blocked. Please reset site permissions in your browser or device settings.
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-destructive bg-destructive/10 p-2 rounded-md font-medium text-center">
            Push notifications are not supported in this browser.
          </p>
        )}

        {isClient && (
          <>
            <DropdownMenuSeparator className="bg-border/60" />
            <button
              onClick={() => router.push('/client/profile?tab=notifications')}
              className="text-xs text-primary hover:underline font-semibold flex items-center justify-center gap-1.5 cursor-pointer py-1"
            >
              Manage in Profile Settings <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>
    </DropdownMenuContent>
  )

  // Client Portal Topbar
  if (isClient) {
    return (
      <header className={cn(
        "relative z-40 flex items-center justify-between border-b border-border/50 bg-background/75 backdrop-blur-xl shrink-0 transition-colors px-4 sm:px-6 pb-3 gap-3 sm:gap-5",
        "pt-safe pt-[max(1rem,calc(env(safe-area-inset-top,0px)+0.5rem))] min-h-[calc(76px+env(safe-area-inset-top,0px))]"
      )}>
        <div className="flex items-center gap-4 flex-1">
          <button
            className="hidden md:flex w-10 h-10 p-0 shrink-0 cursor-pointer"
            onClick={() => setCollapsed(!isCollapsed)}
          >
            <img src="/icons/list-collapse.svg" alt="Menu" width={40} height={40} />
          </button>
          
          <div className="md:hidden">
            <MobileSidebar />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <ClientSearchNavigation />
          <DropdownMenu>
            <DropdownMenuTrigger
              className="relative flex rounded-full border border-border/85 bg-background shadow-sm hover:bg-muted h-10 w-10 shrink-0 p-0 items-center justify-center cursor-pointer focus:outline-none"
            >
              <Bell className="h-5 w-5 text-muted-foreground" />
              {isSubscribed ? (
                <span className="absolute top-2.5 right-2.5 h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              ) : (
                <span className="absolute top-2.5 right-2.5 h-2 w-2 rounded-full bg-amber-500" />
              )}
            </DropdownMenuTrigger>
            {renderNotificationDropdownContent()}
          </DropdownMenu>
          <ThemeToggle className="rounded-full border border-border/85 bg-background shadow-sm hover:bg-muted h-10 w-10 shrink-0 p-0 flex items-center justify-center" />
          
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1.5 focus:outline-none select-none ml-1">
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm text-white border border-border/85 shadow-sm shrink-0 cursor-pointer"
                style={{ backgroundColor: avatarColor }}
              >
                {userInitial}
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuGroup>
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.fullName || user?.firstName || 'User'}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleViewProfile}>
                <User className="mr-2 h-4 w-4" />
                <span>View Profile</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} variant="destructive">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    )
  }

  // Admin Floating Topbar (Matching the Pill Card & Green Gradient Spec)
  return (
    <header className="relative z-40 shrink-0 px-3 sm:px-4 pb-1.5 transition-colors pt-safe pt-[max(1rem,calc(env(safe-area-inset-top,0px)+0.5rem))]">
      <div className="w-full h-14 px-3 sm:px-4 rounded-[16px] bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-white/70 dark:border-white/10 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] flex items-center justify-between gap-3">
        {/* Left Side: Sidebar Toggle + Breadcrumb */}
        <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
          {/* Desktop Sidebar Toggle */}
          <button
            className="hidden md:flex h-9 w-9 rounded-xl bg-slate-100/80 dark:bg-slate-800/80 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 border border-slate-200/50 dark:border-slate-700/50 items-center justify-center text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white transition-colors shadow-2xs shrink-0 cursor-pointer"
            onClick={() => setCollapsed(!isCollapsed)}
            aria-label="Toggle Sidebar"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <line x1="2.5" y1="5" x2="19.5" y2="5" />
              <line x1="2.5" y1="11" x2="13.5" y2="11" />
              <line x1="2.5" y1="17" x2="17.5" y2="17" />
            </svg>
          </button>

          {/* Mobile Sidebar Trigger */}
          <div className="md:hidden">
            <MobileSidebar />
          </div>

          {/* Dynamic Breadcrumbs */}
          <AdminBreadcrumb />
        </div>

        {/* Right Side: Search + Theme + Bell + User Pill */}
        <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
          {/* Search Navigation */}
          <AdminSearchNavigation />

          {/* Subtle Vertical Divider */}
          <div className="h-5 w-px bg-slate-200 dark:bg-slate-700/60 mx-0.5 hidden sm:block" />

          {/* Theme Toggle */}
          <ThemeToggle className="h-9 w-9 rounded-xl bg-slate-100/70 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 flex items-center justify-center transition-colors shadow-2xs [&_svg]:h-4 [&_svg]:w-4 text-slate-600 dark:text-slate-300 shrink-0 cursor-pointer" />

          {/* Notification Bell */}
          <DropdownMenu>
            <DropdownMenuTrigger className="relative h-9 w-9 rounded-xl bg-slate-100/70 dark:bg-slate-800/60 border border-slate-200/60 dark:border-slate-700/60 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 flex items-center justify-center transition-colors shadow-2xs text-slate-600 dark:text-slate-300 shrink-0 cursor-pointer focus:outline-none">
              <Bell className="h-4 w-4" />
              {/* Red badge dot matching screenshot */}
              <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white dark:ring-slate-900" />
            </DropdownMenuTrigger>
            {renderNotificationDropdownContent()}
          </DropdownMenu>

          {/* User Profile Capsule */}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2.5 p-1 pr-2.5 rounded-xl border border-slate-200/60 dark:border-slate-700/60 bg-slate-100/50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 cursor-pointer shadow-2xs focus:outline-none select-none">
              <div
                className="relative h-8 w-8 rounded-full overflow-hidden shrink-0 ring-2 ring-[#00E599] ring-offset-1 ring-offset-white dark:ring-offset-slate-900 flex items-center justify-center font-bold text-sm text-white select-none shadow-xs"
                style={{ backgroundColor: avatarColor }}
              >
                {userInitial}
              </div>
              <div className="hidden sm:flex flex-col text-left leading-tight">
                <span className="text-xs font-bold text-slate-800 dark:text-slate-100 tracking-tight">
                  {displayName}
                </span>
                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-400">
                  {roleDisplay}
                </span>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 ml-0.5 shrink-0" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 p-2 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md">
              <DropdownMenuGroup>
                <DropdownMenuLabel className="px-2 py-1.5">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm text-white shrink-0 select-none shadow-xs"
                      style={{ backgroundColor: avatarColor }}
                    >
                      {userInitial}
                    </div>
                    <div className="flex flex-col space-y-0.5 min-w-0">
                      <p className="text-xs font-bold leading-none text-slate-800 dark:text-slate-100 truncate">
                        {displayName}
                      </p>
                      <p className="text-[11px] leading-none text-slate-400 truncate">{user?.email || 'admin@syraax.com'}</p>
                    </div>
                  </div>
                </DropdownMenuLabel>
              </DropdownMenuGroup>
              <DropdownMenuSeparator className="my-1.5" />
              <DropdownMenuItem onClick={handleViewProfile} className="rounded-lg text-xs cursor-pointer">
                <User className="mr-2 h-3.5 w-3.5" />
                <span>View Profile</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="my-1.5" />
              <DropdownMenuItem onClick={handleLogout} variant="destructive" className="rounded-lg text-xs cursor-pointer text-rose-600 focus:text-rose-600">
                <LogOut className="mr-2 h-3.5 w-3.5" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}

function MobileSidebar() {
  const [open, setOpen] = React.useState(false)
  const pathname = usePathname()
  const router = useRouter()
  const isAdminArea = pathname?.startsWith('/admin')

  // Safety-net fallback: close if the sheet is still open after a route change.
  // The primary close happens eagerly in handleItemClick via router.push.
  React.useEffect(() => {
    setOpen(false)
  }, [pathname])

  const handleItemClick = React.useCallback((href?: string) => {
    // Strategy: navigate programmatically FIRST (queues the route change in the
    // Next.js router), then close the sheet immediately.
    //
    // This avoids two production bugs:
    //   1. Old approach (close on click via Link): unmounted the <Link> before
    //      the router committed navigation → first click no-op, second = reload.
    //   2. Previous fix (close via pathname effect): kept the sheet open during
    //      the page transition → framer-motion AnimatePresence conflicted with
    //      React's reconciler → "Cannot read properties of null (removeChild)".
    //
    // By using router.push here the Link's own navigation is prevented
    // (via e.preventDefault in the sidebar), so closing the sheet is safe.
    if (href && !href.startsWith('http') && !href.startsWith('//')) {
      router.push(href)
    }
    setOpen(false)
  }, [router])

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        className="h-10 w-10 rounded-xl bg-slate-100/80 dark:bg-slate-800/80 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 border border-slate-200/50 dark:border-slate-700/50 flex items-center justify-center text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white transition-all shadow-2xs shrink-0 cursor-pointer touch-manipulation select-none active:scale-95"
        aria-label="Open menu"
      >
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
          <line x1="2.5" y1="5" x2="19.5" y2="5" />
          <line x1="2.5" y1="11" x2="13.5" y2="11" />
          <line x1="2.5" y1="17" x2="17.5" y2="17" />
        </svg>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="p-0 bg-transparent border-0 shadow-none w-[285px] pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)]"
        showCloseButton={false}
      >
        <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
        {isAdminArea ? (
          <AdminSidebar forceExpanded onItemClick={handleItemClick} />
        ) : (
          <UserSidebar forceExpanded onItemClick={handleItemClick} />
        )}
      </SheetContent>
    </Sheet>
  )
}
